import CloudUpload from '@mui/icons-material/CloudUpload';
import { Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { MAX_FILE_SIZE_IN_BYTE } from '../../constants';
import { AppConstantsContext, UserContext } from '../../context';
import { TranscriptionDialogContext } from '../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { JobType, SheetSourceType } from '../../entities';
import {
  SUPPORTED_FILE_TYPES,
  SUPPORTED_SCAN_TYPES,
  TrackingAction,
  TrackingCategory,
  TrackingTranscriptionParams,
  trackingService,
} from '../../services';
import { FileCheckResult, completeCheck, mapResultToErrorText } from '../../utils/fileHelper';

const useStyles = makeStyles()((theme) => ({
  container: {
    height: '100%',
    padding: theme.spacing(),
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  dropZone: {
    background: '#252525',
    border: `2px dashed ${theme.palette.grey['500']}`,
    boxSizing: 'border-box',
    width: '100%',
    height: 'fit-content',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
    containerType: 'inline-size',
  },
  dropZoneActive: {
    background: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    border: `2px dashed ${theme.palette.primary.main}`,
  },
  dropZoneError: {
    background: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    border: `2px dashed ${theme.palette.error.light}`,
  },

  infoContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 5fr',
    alignItems: 'center',
    gap: '.5em',
    '@container (max-width: 350px)': {
      gridTemplateColumns: '1fr',
      justifyItems: 'center',
      textAlign: 'center',
    },
  },
  infoRow: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 2,
    '& .MuiButton-label': {
      display: 'block',
      padding: 0,
    },
  },
  [theme.breakpoints.down('xs')]: {
    infoRowOptional: {
      display: 'none',
    },
  },
  error: {
    color: theme.palette.error.main,
  },
  hint: {
    fontSize: '0.7em',
    textAlign: 'center',
  },
  title: {
    '@container (max-width: 350px)': {
      fontSize: '1.1em',
    },
  },
}));

export interface FileDropProps {
  allowMultipleFiles?: boolean;
  maximumFileSizeBytes?: number;
  hint?: JSX.Element | string;
  openDialog: () => void;
  location?: (typeof TrackingTranscriptionParams.location)['songbook' | 'wp' | 'nav'];
}

/**
 * Component for a generalized file drop field with shallow
 * and deep file type checking, with optional manual upload
 */
const FileDrop: React.FC<FileDropProps> = ({
  openDialog,
  hint,
  maximumFileSizeBytes = MAX_FILE_SIZE_IN_BYTE,
  allowMultipleFiles = false,
  location,
}) => {
  const { classes } = useStyles();
  const { setApriori, setFile, setSource } = React.useContext(TranscriptionDialogContext);
  const { jobType } = React.useContext(AppConstantsContext);
  const { user } = React.useContext(UserContext);
  const { t } = useTranslation();

  const [error, setError] = useState<FileCheckResult>(FileCheckResult.OK);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>();

  // file submit
  const onFilesSubmit = useCallback(
    async (files?: FileList) => {
      setError(FileCheckResult.OK);
      let checkResult: FileCheckResult;
      if (jobType === JobType.TranscribeScan) {
        checkResult = await completeCheck(
          allowMultipleFiles,
          SUPPORTED_SCAN_TYPES,
          maximumFileSizeBytes,
          files,
        );
      } else {
        checkResult = await completeCheck(
          allowMultipleFiles,
          SUPPORTED_FILE_TYPES,
          maximumFileSizeBytes,
          files,
        );
      }

      if (checkResult !== FileCheckResult.OK) {
        setError(checkResult);
        return;
      }

      const fileData = files[0];
      if (!fileData) return;
      trackingService.valueObjectEvent<{ file_type: string }>(
        TrackingCategory.Transcription,
        TrackingAction.UPLOAD_FILE,
        {
          file_type: files[0]?.type,
        },
      );
      const trackingValues = {
        location,
        input: TrackingTranscriptionParams.input.file,
      };
      trackingService.valueObjectEvent<{ location: string; input: string }>(
        TrackingCategory.Transcription,
        TrackingTranscriptionParams.action.start,
        {
          ...trackingValues,
        },
      );
      // console.log(TrackingTranscriptionParams.action.start, { ...trackingValues });
      setApriori({
        composer: user ? `${user.firstName} ${user.lastName}` : 'unknown',
        title:
          fileData.name.replace(`.${fileData.name.split('.').slice(-1)[0]}`, '')
          || `My Song ${new Date().toLocaleString()}`,
      });
      setFile(fileData);
      setSource(SheetSourceType.File);
      try {
        if (
          (jobType === JobType.TranscribeScan && !SUPPORTED_SCAN_TYPES.includes(fileData?.type))
          || (jobType !== JobType.TranscribeScan && !SUPPORTED_FILE_TYPES.includes(fileData?.type))
        ) {
          throw new Error(`Filetype ${fileData?.type} is not supported.`);
        }

        openDialog();
      } catch (e) {
        console.log(`file drop error:`);
      }
    },
    [openDialog, setError, allowMultipleFiles, maximumFileSizeBytes, user, setApriori, setFile],
  );

  // file drop event
  const onDrop = useCallback(
    (evt: React.DragEvent<HTMLDivElement>) => {
      // prevent browser from opening file
      evt.preventDefault();
      setDragOver(false);

      const { files } = evt.dataTransfer;

      onFilesSubmit(files);
    },
    [setDragOver, onFilesSubmit],
  );

  const onDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // prevent browser from opening file
      e.preventDefault();
      setError(undefined);

      // check if file type is supported to give immediate feedback

      if (e.dataTransfer.items?.length >= 1) {
        if (
          (!SUPPORTED_SCAN_TYPES.includes(e.dataTransfer.items[0].type)
            && jobType === JobType.TranscribeScan)
          || (jobType !== JobType.TranscribeScan
            && !SUPPORTED_FILE_TYPES.includes(e.dataTransfer.items[0].type))
        ) {
          setError(FileCheckResult.INVALID_FILE_TYPE);
        }
      }
      console.log(e.dataTransfer.items[0]);
      setDragOver(true);
    },
    [setDragOver, setError],
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // prevent browser from opening file
    e.preventDefault();
  }, []);

  const onStopDrag = useCallback(() => {
    setError(undefined);
    setDragOver(false);
  }, [setDragOver, setError]);

  const errorText = useMemo(() => mapResultToErrorText(error, t, maximumFileSizeBytes), [error, t]);

  return (
    <div id="btn_demo_upload_file" className={classes.container}>
      <div
        className={`${classes.dropZone} ${dragOver ? classes.dropZoneActive : ''} ${
          error ? classes.dropZoneError : ''
        }`}
        onDrop={onDrop}
        onDragEnter={onDragEnter}
        onDragEnd={onStopDrag}
        onDragLeave={onStopDrag}
        onDragOver={onDragOver}
        onClick={() => {
          fileInputRef.current.value = null;
          fileInputRef.current.click();
        }}
        aria-hidden="true"
      >
        <div
          style={{
            pointerEvents: dragOver ? 'none' : 'all',
          }}
        >
          <div className={classes.infoContainer}>
            <CloudUpload style={{ fontSize: 60 }} color="primary" />
            <div className={classes.infoRow}>
              {errorText ? <span className={classes.error}>{errorText}</span> : ''}
              <Typography variant="h6" component="span" className={classes.title}>
                {jobType === JobType.TranscribeScan
                  ? t('home.input.drop_file_scan')
                  : t('home.input.drop_file')}
              </Typography>
              <Button variant="text" style={{ textAlign: 'left', padding: 0 }} color="primary">
                {jobType === JobType.TranscribeScan
                  ? t('home.input.choose_file')
                  : t('home.input.choose_file_audio')}
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                multiple={allowMultipleFiles}
                hidden
                accept={
                  jobType === JobType.TranscribeScan
                    ? SUPPORTED_SCAN_TYPES.join(',')
                    : SUPPORTED_FILE_TYPES.join(',')
                }
                onInput={() => onFilesSubmit(fileInputRef.current.files)}
              />
              <div className={classes.infoRow}>
                <Typography className={classes.hint} variant="body2">
                  {hint}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileDrop;
